import { Message } from '../components/features/chat/types';

/**
 * Stable identity for an annotated image across signings.
 *
 * The same artifact is re-signed on every history reload and on each frame that
 * carries it, so the query string differs while the object is identical. Keying
 * on the path alone is what stops one marked photo from rendering three times.
 */
export function annotatedImageKey(url: string): string {
  if (!url) return '';
  let path: string;
  try {
    path = new URL(url, 'https://x.invalid').pathname;
  } catch {
    path = url.split('?')[0];
  }
  // The artifact's FILE NAME is the identity. The rest of the path is storage
  // layout that differs between the live frame and a history reload for the
  // very same image: private vs public bucket, user- vs session-scoped folder,
  // and the trailing ADK version segment (".../annotated_damage_image_<id>.png/0").
  // Keyed on the full path, one photo looked like two and rendered twice.
  const segments = decodeURIComponent(path).split('/').filter(Boolean);
  const named = segments.find(seg => /^annotated[^/]*\.(png|jpe?g|webp)$/i.test(seg));
  return named || path;
}

/** How long a signed URL is reused for the same image (the backend signs for 24 h). */
const STABLE_URL_MAX_AGE_MS = 6 * 60 * 60 * 1000;
const stableUrls = new Map<string, { url: string; at: number }>();

/**
 * The FIRST signed URL seen for this image, while it is still fresh.
 *
 * A history reload a few seconds after a turn (parts / complementary
 * hydration, order creation) re-signs every annotated image. A new query
 * string is a new `src`: the browser drops the decoded photo and downloads it
 * again, so the marked photo vanished for a few seconds and came back. Reusing
 * the first URL keeps the same `src`, which the browser already has.
 */
export function stableAnnotatedUrl(url: string, now: number = Date.now()): string {
  const key = annotatedImageKey(url);
  if (!key) return url;
  const seen = stableUrls.get(key);
  if (seen && now - seen.at < STABLE_URL_MAX_AGE_MS) return seen.url;
  stableUrls.set(key, { url, at: now });
  return url;
}

/** Test hook: forget every remembered URL. */
export function resetStableAnnotatedUrls(): void {
  stableUrls.clear();
}

/** Hidden extraction_result bubble — never rendered, but it can carry images. */
function isHiddenExtractionBubble(msg: Message): boolean {
  try {
    const outer = JSON.parse(msg.text || '{}');
    return outer?.type !== undefined && outer?.action !== undefined;
  } catch {
    return false;
  }
}

/**
 * A transient status pill ("Analyzing your image…", "Identifying VIN…").
 *
 * These are real bubbles in the live stream — the ACK text is frozen onto its own
 * message — but they are progress notices, not findings, so the markings must
 * never land on one. Mid-stream the narrative frame that will claim the images
 * does not exist yet, and without this the photo attaches to the ACK and then
 * jumps down the thread when the narrative arrives.
 */
function isStatusOnlyBubble(msg: Message): boolean {
  const hasAck = (msg.events ?? []).some((e: any) => e?.type === 'ack');
  if (!hasAck) return false;
  return (msg.parts_results?.length ?? 0) === 0 && (msg.annotated_images?.length ?? 0) === 0;
}

/**
 * Decide which bot bubble renders which annotated images — each image exactly once.
 *
 * Anchor rule: **the first visible bot bubble that carries the image itself wins.**
 *
 * The backend already picks the right bubble. `turn_runner` routes a turn's
 * annotated images onto the validation / HITL / attachment-narrative frame — the
 * "From your image, I can see moderate dent damage…" message — and the markings
 * belong directly under that sentence. On a history reload the images are
 * resolved per *invocation*, so the later results bubble carries them too; taking
 * the first carrier keeps them next to the narrative instead of stranding them
 * below the results table.
 *
 * Only images that NO visible bubble carries (the hidden extraction bubble
 * produced them and no frame claimed them) fall back to the next visible bubble,
 * so a turn that ends in a question still shows its markings. That fallback must
 * never be the first thing after the upload: status ACKs like "Analyzing your
 * image…" carry no images, so they are skipped by construction here.
 */
export function buildAnnotatedImageAssignments(messages: Message[]): Map<string, string[]> {
  const urlByKey = new Map<string, string>();
  const anchorByKey = new Map<string, string>();

  // Pass 1 — first visible carrier of each image.
  for (const msg of messages) {
    if (msg.sender !== 'bot' || isHiddenExtractionBubble(msg)) continue;
    for (const url of msg.annotated_images ?? []) {
      const key = annotatedImageKey(url);
      if (!key || anchorByKey.has(key)) continue;
      urlByKey.set(key, url);
      anchorByKey.set(key, msg.id);
    }
  }

  // Pass 2 — hidden-only images ride forward to the next visible bubble.
  let pending: string[] = [];
  for (const msg of messages) {
    if (msg.sender !== 'bot') continue;
    if (isHiddenExtractionBubble(msg)) {
      for (const url of msg.annotated_images ?? []) {
        const key = annotatedImageKey(url);
        if (key && !anchorByKey.has(key)) pending.push(url);
      }
      continue;
    }
    // Hold the images past progress notices — they belong on a finding.
    if (isStatusOnlyBubble(msg)) continue;
    for (const url of pending) {
      const key = annotatedImageKey(url);
      if (!key || anchorByKey.has(key)) continue;
      urlByKey.set(key, url);
      anchorByKey.set(key, msg.id);
    }
    pending = [];
  }

  // Assemble, preserving the order the images appear in the thread.
  // (Map#forEach rather than for..of: the build targets ES5, where iterating a
  // Map directly needs --downlevelIteration.)
  const assignments = new Map<string, string[]>();
  anchorByKey.forEach((messageId, key) => {
    const signed = urlByKey.get(key);
    const url = signed ? stableAnnotatedUrl(signed) : signed;
    if (!url) return;
    const list = assignments.get(messageId);
    if (list) list.push(url);
    else assignments.set(messageId, [url]);
  });
  return assignments;
}

/**
 * Merge a history reload into the messages on screen without losing a marked
 * photo the backend has not saved yet.
 *
 * A few seconds after a turn the chat reloads history on purpose (parts /
 * complementary hydration, order creation, background-processing poll). The
 * reload REPLACES the list, and the bubble that carried the marked photo is
 * often not in `conversation_history` yet: the whole message vanished and came
 * back on the next reload. Here, every live bot bubble of the current turn that
 * shows a marked photo NO loaded message carries is kept — right after the
 * turn's user message — until history catches up and carries it itself.
 *
 * Only for the same conversation: the last user message must read the same on
 * both sides, otherwise the loaded list wins untouched.
 */
export function keepUnsavedPhotoBubbles(current: Message[], loaded: Message[]): Message[] {
  const lastUserIdx = (list: Message[]) => {
    for (let i = list.length - 1; i >= 0; i -= 1) if (list[i].sender === 'user') return i;
    return -1;
  };
  const curUser = lastUserIdx(current);
  const loadUser = lastUserIdx(loaded);
  if (curUser < 0 || loadUser < 0) return loaded;
  if ((current[curUser].text || '').trim() !== (loaded[loadUser].text || '').trim()) return loaded;

  const savedKeys = new Set<string>();
  for (const m of loaded) for (const u of m.annotated_images ?? []) savedKeys.add(annotatedImageKey(u));

  // A live bubble whose TEXT history already has for this turn is saved,
  // whatever its image URL looks like — keeping it would show it twice.
  const savedTexts = new Set(
    loaded.slice(loadUser + 1).filter(m => m.sender === 'bot').map(m => (m.text || '').trim()).filter(Boolean),
  );
  const unsaved = current.slice(curUser + 1).filter(m =>
    m.sender === 'bot'
    && !m.isStreaming
    && !isHiddenExtractionBubble(m)
    && !savedTexts.has((m.text || '').trim())
    && (m.annotated_images ?? []).length > 0
    && (m.annotated_images ?? []).every(u => !savedKeys.has(annotatedImageKey(u))),
  );
  if (!unsaved.length) return loaded;
  return [...loaded.slice(0, loadUser + 1), ...unsaved, ...loaded.slice(loadUser + 1)];
}
